import React, { useState, useRef, useEffect } from 'react';
import CalcShell from '../CalcShell';

/*
  Scientific Calculator — Calculator.net button-grid replica.
  Buttons match the reference image exactly.
*/

const factorial = (n) => {
  if (n < 0 || !Number.isInteger(n)) return NaN;
  if (n > 170) return Infinity;
  let r = 1; for (let i = 2; i <= n; i++) r *= i; return r;
};

export default function ScientificCalc({ hideHeader = false }) {
  const [expr, setExpr] = useState('');
  const [display, setDisplay] = useState('0');
  const [mode, setMode] = useState('deg'); // deg | rad
  const [memory, setMemory] = useState(0);
  const [ans, setAns] = useState(0);
  const [waitingFor, setWaitingFor] = useState(null); // pending unary like sin(
  const displayRef = useRef(null);

  useEffect(() => {
    if (displayRef.current) displayRef.current.scrollLeft = displayRef.current.scrollWidth;
  }, [expr]);

  const push = (s) => {
    setExpr(e => (e === '0' ? s : e + s));
    setDisplay(d => (d === '0' && /[0-9.]/.test(s) ? s : d + s));
  };
  const pushOp = (op) => setExpr(e => e + op);
  const pushWrap = (fn) => setExpr(e => e + fn + '(');

  const backspace = () => { setExpr(e => e.slice(0, -1)); setDisplay(d => d.length > 1 ? d.slice(0, -1) : '0'); };
  const clearAll = () => { setExpr(''); setDisplay('0'); };
  const toggleSign = () => {
    // Negate the last number in the expression
    const m = expr.match(/(-?\d*\.?\d+)$/);
    if (!m) { setExpr('-' + expr); return; }
    const idx = expr.length - m[0].length;
    const before = expr.slice(0, idx);
    const negated = m[0].startsWith('-') ? m[0].slice(1) : '-' + m[0];
    setExpr(before + negated);
    setDisplay(negated);
  };
  const round2 = () => {
    try {
      const v = compute(expr || display, mode, memory, ans);
      setExpr(String(Math.round(v)));
      setDisplay(String(Math.round(v)));
    } catch { /* ignore */ }
  };
  const equals = () => {
    try {
      const v = compute(expr || display, mode, memory, ans);
      setAns(v);
      setDisplay(String(v));
      setExpr(String(v));
    } catch { setDisplay('Error'); }
  };

  const memStore = (delta) => {
    try {
      const v = compute(expr || display, mode, memory, ans);
      setMemory(m => m + delta * v);
    } catch { /* ignore */ }
  };
  const memRecall = () => { setExpr(e => e + String(memory)); setDisplay(String(memory)); };

  const btn = (label, onClick, cls = '', testid = null) => (
    <button
      onClick={onClick}
      data-testid={testid || `sci-btn-${label.replace(/[^a-z0-9]/gi, '')}`}
      className={`h-14 rounded-md border border-slate-300 font-semibold text-slate-800 text-lg select-none active:scale-95 transition-transform ${cls}`}
    >{label}</button>
  );
  const digit = 'bg-gradient-to-b from-slate-200 to-slate-300 hover:from-slate-300 hover:to-slate-400';
  const fn = 'bg-white hover:bg-slate-100';
  const acc = 'bg-gradient-to-b from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300';

  return (
    <CalcShell look="cnet" hideHeader={hideHeader} title="Scientific Calculator" subtitle="Perform advanced arithmetic — sin/cos/tan, inverse trig, roots, logs, exponents, factorial, memory and last-answer recall.">
      <div className={hideHeader ? "w-full p-2" : "mx-auto max-w-md bg-white p-4 rounded-xl border border-slate-200 shadow-sm"}>
        {/* Display */}
        <div ref={displayRef} className="bg-[#325487] text-white text-right font-mono text-3xl px-4 py-4 rounded-md mb-3 overflow-x-auto whitespace-nowrap" data-testid="sci-display">
          {expr || display}
        </div>

        {/* Row 1: sin cos tan + Deg/Rad */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('sin', () => pushWrap('sin'), fn)}
          {btn('cos', () => pushWrap('cos'), fn)}
          {btn('tan', () => pushWrap('tan'), fn)}
          <label className="col-span-2 flex items-center justify-center gap-2 border border-slate-200 rounded-md bg-white text-sm">
            <input type="radio" checked={mode === 'deg'} onChange={() => setMode('deg')} data-testid="sci-mode-deg" /> Deg
            <input type="radio" checked={mode === 'rad'} onChange={() => setMode('rad')} data-testid="sci-mode-rad" className="ml-2" /> Rad
          </label>
        </div>
        {/* Row 2: sin-1 cos-1 tan-1 π e */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('sin⁻¹', () => pushWrap('asin'), fn, 'sci-btn-asin')}
          {btn('cos⁻¹', () => pushWrap('acos'), fn, 'sci-btn-acos')}
          {btn('tan⁻¹', () => pushWrap('atan'), fn, 'sci-btn-atan')}
          {btn('π', () => push('π'), fn)}
          {btn('e', () => push('e'), fn)}
        </div>
        {/* Row 3: xʸ x³ x² eˣ 10ˣ */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('xʸ', () => pushOp('^'), fn, 'sci-btn-pow')}
          {btn('x³', () => pushOp('^3'), fn, 'sci-btn-cube')}
          {btn('x²', () => pushOp('^2'), fn, 'sci-btn-square')}
          {btn('eˣ', () => pushWrap('exp'), fn, 'sci-btn-expx')}
          {btn('10ˣ', () => pushWrap('ten'), fn, 'sci-btn-tenx')}
        </div>
        {/* Row 4: ʸ√x ³√x √x ln log */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('ʸ√x', () => pushOp('root'), fn, 'sci-btn-yroot')}
          {btn('³√x', () => pushWrap('cbrt'), fn, 'sci-btn-croot')}
          {btn('√x', () => pushWrap('sqrt'), fn, 'sci-btn-sqrt')}
          {btn('ln', () => pushWrap('ln'), fn)}
          {btn('log', () => pushWrap('log'), fn)}
        </div>
        {/* Row 5: ( ) 1/x % n! */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('(', () => push('('), fn)}
          {btn(')', () => push(')'), fn)}
          {btn('1/x', () => pushWrap('inv'), fn, 'sci-btn-inv')}
          {btn('%', () => push('%'), fn)}
          {btn('n!', () => pushOp('!'), fn, 'sci-btn-fact')}
        </div>
        {/* Row 6: 7 8 9 + Back */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('7', () => push('7'), digit)}
          {btn('8', () => push('8'), digit)}
          {btn('9', () => push('9'), digit)}
          {btn('+', () => pushOp('+'), fn)}
          {btn('Back', backspace, fn, 'sci-btn-back')}
        </div>
        {/* Row 7: 4 5 6 − Ans */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('4', () => push('4'), digit)}
          {btn('5', () => push('5'), digit)}
          {btn('6', () => push('6'), digit)}
          {btn('−', () => pushOp('-'), fn, 'sci-btn-minus')}
          {btn('Ans', () => push(String(ans)), fn, 'sci-btn-ans')}
        </div>
        {/* Row 8: 1 2 3 × M+ */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('1', () => push('1'), digit)}
          {btn('2', () => push('2'), digit)}
          {btn('3', () => push('3'), digit)}
          {btn('×', () => pushOp('*'), fn, 'sci-btn-mult')}
          {btn('M+', () => memStore(1), fn, 'sci-btn-mplus')}
        </div>
        {/* Row 9: 0 . EXP ÷ M− */}
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {btn('0', () => push('0'), digit)}
          {btn('.', () => push('.'), digit)}
          {btn('EXP', () => pushOp('E'), fn, 'sci-btn-exp')}
          {btn('÷', () => pushOp('/'), fn, 'sci-btn-div')}
          {btn('M−', () => memStore(-1), fn, 'sci-btn-mminus')}
        </div>
        {/* Row 10: ± RND AC = MR */}
        <div className="grid grid-cols-5 gap-1.5">
          {btn('±', toggleSign, fn, 'sci-btn-sign')}
          {btn('RND', round2, fn, 'sci-btn-rnd')}
          {btn('AC', clearAll, acc, 'sci-btn-ac')}
          {btn('=', equals, acc, 'sci-btn-equals')}
          {btn('MR', memRecall, fn, 'sci-btn-mr')}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-slate-500 px-1">
          <span>Memory: <b data-testid="sci-memory">{memory}</b></span>
          <span>Ans: <b data-testid="sci-ans">{ans}</b></span>
          <span>Mode: <b>{mode.toUpperCase()}</b></span>
        </div>
      </div>
    </CalcShell>
  );
}

/* -------- Expression evaluator (tokenize → RPN → eval) -------- */
function compute(input, mode, memory, ans) {
  if (input == null || input === '') throw new Error('empty');
  // Replace factorial "!" with func call — a! becomes fact(a)
  let s = String(input);
  // Repeatedly replace trailing "!" on numbers or parenthesized groups
  const factRegex = /(\d+(?:\.\d+)?|\([^()]*\))!/;
  while (factRegex.test(s)) s = s.replace(factRegex, 'fact($1)');
  // yroot: NUMroot NUM  →  root(NUM,NUM)
  s = s.replace(/(\d+(?:\.\d+)?|\([^()]*\))root(\d+(?:\.\d+)?|\([^()]*\))/g, 'root($1,$2)');
  // ^2 and ^3 handled naturally by tokenizer via ^ operator
  // Replace π, e constants
  s = s.replace(/π/g, 'PI').replace(/(?<![a-z])e(?![a-z])/gi, 'E');
  s = s.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
  const tokens = tokenize(s);
  const rpn = toRPN(tokens);
  const v = evalRPN(rpn, mode);
  if (!isFinite(v)) return v;
  return Math.abs(v) < 1e-12 ? 0 : Math.round(v * 1e12) / 1e12;
}
function tokenize(s) {
  const out = []; let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (ch === ' ') { i++; continue; }
    if (/[0-9.]/.test(ch)) {
      let j = i; while (j < s.length && /[0-9.]/.test(s[j])) j++;
      out.push({ t: 'num', v: parseFloat(s.slice(i, j)) }); i = j; continue;
    }
    if (/[A-Za-z]/.test(ch)) {
      let j = i; while (j < s.length && /[A-Za-z]/.test(s[j])) j++;
      const word = s.slice(i, j);
      if (word === 'PI') out.push({ t: 'num', v: Math.PI });
      else if (word === 'E') out.push({ t: 'num', v: Math.E });
      else out.push({ t: 'fn', v: word });
      i = j; continue;
    }
    if ('+-*/^,'.includes(ch)) { out.push({ t: 'op', v: ch }); i++; continue; }
    if (ch === '(') { out.push({ t: 'lp' }); i++; continue; }
    if (ch === ')') { out.push({ t: 'rp' }); i++; continue; }
    i++;
  }
  return out;
}
const PREC = { '+': 1, '-': 1, '*': 2, '/': 2, '^': 3 };
const RIGHT = { '^': true };
function toRPN(tokens) {
  const out = [], op = [];
  for (let k = 0; k < tokens.length; k++) {
    const t = tokens[k];
    if (t.t === 'num') out.push(t);
    else if (t.t === 'fn') op.push(t);
    else if (t.t === 'op') {
      // Unary minus at start or after operator/lp: convert to num 0 push then op
      if (t.v === '-' && (k === 0 || tokens[k - 1].t === 'op' || tokens[k - 1].t === 'lp' || tokens[k - 1].v === ',')) {
        out.push({ t: 'num', v: 0 });
      }
      while (op.length) {
        const top = op[op.length - 1];
        if (top.t === 'fn') { out.push(op.pop()); continue; }
        if (top.t === 'op' && (PREC[top.v] > PREC[t.v] || (PREC[top.v] === PREC[t.v] && !RIGHT[t.v]))) { out.push(op.pop()); continue; }
        break;
      }
      op.push(t);
    } else if (t.t === 'lp') op.push(t);
    else if (t.t === 'rp') {
      while (op.length && op[op.length - 1].t !== 'lp') out.push(op.pop());
      op.pop();
      if (op.length && op[op.length - 1].t === 'fn') out.push(op.pop());
    }
  }
  while (op.length) out.push(op.pop());
  return out;
}
function evalRPN(rpn, mode) {
  const st = [];
  const toRad = x => mode === 'deg' ? x * Math.PI / 180 : x;
  const fromRad = x => mode === 'deg' ? x * 180 / Math.PI : x;
  for (const t of rpn) {
    if (t.t === 'num') st.push(t.v);
    else if (t.t === 'op') {
      const b = st.pop(), a = st.pop();
      if (t.v === '+') st.push(a + b);
      else if (t.v === '-') st.push(a - b);
      else if (t.v === '*') st.push(a * b);
      else if (t.v === '/') st.push(a / b);
      else if (t.v === '^') st.push(Math.pow(a, b));
      else if (t.v === ',') { st.push(a); st.push(b); }
    } else if (t.t === 'fn') {
      const a = st.pop();
      switch (t.v) {
        case 'sin': st.push(Math.sin(toRad(a))); break;
        case 'cos': st.push(Math.cos(toRad(a))); break;
        case 'tan': st.push(Math.tan(toRad(a))); break;
        case 'asin': st.push(fromRad(Math.asin(a))); break;
        case 'acos': st.push(fromRad(Math.acos(a))); break;
        case 'atan': st.push(fromRad(Math.atan(a))); break;
        case 'sqrt': st.push(Math.sqrt(a)); break;
        case 'cbrt': st.push(Math.cbrt(a)); break;
        case 'ln': st.push(Math.log(a)); break;
        case 'log': st.push(Math.log10(a)); break;
        case 'exp': st.push(Math.exp(a)); break;
        case 'ten': st.push(Math.pow(10, a)); break;
        case 'inv': st.push(1 / a); break;
        case 'fact': st.push(factorial(a)); break;
        case 'root': { const n = a; const x = st.pop(); st.push(Math.pow(x, 1 / n)); break; }
        default: throw new Error('unknown fn ' + t.v);
      }
    }
  }
  if (st.length !== 1) throw new Error('bad expr');
  return st[0];
}
